import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ExternalLink, Users, Globe, Telescope } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PageLoader } from '@shared/components/PageLoader';
import { prospectsAPI } from '@/utils/supabase/client';
import type { Prospect, ProspectStatus } from '@/types/prospects';
import { ProspectTierBadge, ProspectStatusBadge } from './ProspectTierBadge';
import { cn } from '@/components/ui/utils';

const PRIORITY_FILTERS = [
  { label: 'All', value: '' },
  { label: 'A — Pursue', value: 'A: pursue first' },
  { label: 'B — Strong', value: 'B: strong candidate' },
  { label: 'C — Validate', value: 'C: validate and watchlist' },
  { label: 'D — Speculative', value: 'D: speculative or local/service' },
  { label: 'F — Referral only', value: 'F: referral node, not prospect' },
];

const STATUS_FILTERS: { label: string; value: ProspectStatus | '' }[] = [
  { label: 'All status', value: '' },
  { label: 'Prospect', value: 'prospect' },
  { label: 'Deck Building', value: 'deck_building' },
  { label: 'Deck Ready', value: 'deck_ready' },
  { label: 'Outreach Sent', value: 'outreach_sent' },
  { label: 'Meeting Booked', value: 'meeting_booked' },
  { label: 'Disqualified', value: 'disqualified' },
];

function ScorePill({ label, value }: { label: string; value: number | null }) {
  if (value == null) return null;
  const color = value >= 80 ? 'text-success bg-success/15' :
                value >= 65 ? 'text-info bg-info/15' :
                'text-muted-foreground bg-muted';
  return (
    <span className={cn('inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs font-medium', color)}>
      <span className="text-xs opacity-60">{label}</span>
      <span>{value}</span>
    </span>
  );
}

export function ProspectsList() {
  const navigate = useNavigate();
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const loadedOnce = useRef(false);
  const didInit = useRef(false);
  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProspectStatus | ''>('');

  const load = useCallback((q?: string, tier?: string, status?: ProspectStatus | '') => {
    // Only the first load swaps the list out for <PageLoader />. Blanking the
    // list on every debounced search keystroke throws away the scroll position
    // and flashes the whole page.
    if (!loadedOnce.current) setLoading(true);
    prospectsAPI
      .getAll({
        q: q && q.length >= 2 ? q : undefined,
        priority_tier: tier || undefined,
        status: (status as ProspectStatus) || undefined,
      })
      .then(({ prospects: p, total: t }) => {
        setProspects(p);
        setTotal(t);
      })
      .catch(console.error)
      .finally(() => { loadedOnce.current = true; setLoading(false); });
  }, []);

  useEffect(() => {
    // One effect owns loading. The first run fires immediately so the page isn't
    // blank for the debounce interval; every run after that debounces keystrokes.
    if (!didInit.current) {
      didInit.current = true;
      load(search, priorityFilter, statusFilter);
      return;
    }
    const timer = setTimeout(() => load(search, priorityFilter, statusFilter), 300);
    return () => clearTimeout(timer);
  }, [search, priorityFilter, statusFilter, load]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="border-b px-4 lg:px-6 py-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48 max-w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search brand, category, contact…"
            className="pl-9"
          />
        </div>

        {/* Priority filter chips */}
        <div className="flex items-center gap-1 flex-wrap">
          {PRIORITY_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setPriorityFilter(f.value)}
              className={cn(
                'px-2.5 py-1 rounded-full text-xs font-medium border transition-colors',
                priorityFilter === f.value
                  ? 'bg-foreground text-background border-foreground'
                  : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/50'
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Status filter */}
        <Select
          value={statusFilter || 'all'}
          onValueChange={(v) => setStatusFilter(v === 'all' ? '' : (v as ProspectStatus))}
        >
          <SelectTrigger size="sm" className="w-auto text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_FILTERS.map((f) => (
              <SelectItem key={f.value || 'all'} value={f.value || 'all'} className="text-xs">
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span className="text-xs text-muted-foreground ml-auto whitespace-nowrap">
          {loading ? '…' : `${total} prospects`}
        </span>
      </div>

      {/* List */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <PageLoader />
        ) : prospects.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground">
            <Telescope className="h-10 w-10 opacity-30" />
            <p className="text-sm">No prospects found</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground w-8">Tier</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Brand</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground hidden md:table-cell">Category</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground hidden lg:table-cell">Connection</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground hidden xl:table-cell">Scores</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-2.5 w-8" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {prospects.map((p) => (
                <tr
                  key={p.id}
                  className="hover:bg-muted/30 cursor-pointer transition-colors group"
                  onClick={() => navigate(`/prospects/${p.id}`)}
                >
                  <td className="px-4 py-3">
                    <ProspectTierBadge tier={p.priority_tier} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium">{p.brand}</span>
                      {p.website && (
                        <span className="text-xs text-muted-foreground truncate max-w-48">
                          {p.website.replace(/^https?:\/\//, '')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className="text-muted-foreground text-xs line-clamp-2 max-w-48">{p.category}</span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {p.sean_matched_contact ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-medium">{p.sean_matched_contact}</span>
                        {p.sean_contact_title && (
                          <span className="text-xs text-muted-foreground">{p.sean_contact_title}</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden xl:table-cell">
                    <div className="flex items-center gap-1 flex-wrap">
                      <ScorePill label="ICP" value={p.icp_fit_score} />
                      <ScorePill label="Land" value={p.landability_score} />
                      {p.final_priority_score != null && (
                        <span className="text-xs font-semibold text-foreground tabular-nums">
                          {p.final_priority_score.toFixed(1)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <ProspectStatusBadge status={p.status} />
                  </td>
                  <td className="px-4 py-3">
                    {p.website && (
                      <a
                        href={p.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
